/* eslint-disable @typescript-eslint/no-unused-vars, no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  FormHelperText,
} from '@mui/material';
import { logger } from '@musedrops/shared';
import type { HostPersona } from '@musedrops/shared';
import { HostPersonaApi } from '../../services/hostPersonaApi';

interface HostPersonaSelectorProps {
  value: string | null;
  onChange: (personaKey: string | null) => void;
  label?: string;
  required?: boolean;
  helperText?: string;
}

export const HostPersonaSelector: React.FC<HostPersonaSelectorProps> = ({
  value,
  onChange,
  label = 'Host Persona',
  required = false,
  helperText,
}) => {
  const [personas, setPersonas] = useState<HostPersona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const loadPersonas = async () => {
      try {
        setLoading(true);
        const allPersonas = await HostPersonaApi.getAll();
        setPersonas(allPersonas);
        setError('');
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load host personas'
        );
        logger.error('Failed to load host personas', { error: err });
      } finally {
        setLoading(false);
      }
    };

    loadPersonas();
  }, []);

  // Find the selected persona for display of goodFor
  const selectedPersona = personas.find(p => p.key === value);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <FormControl fullWidth required={required}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={value || ''}
          onChange={e =>
            onChange(e.target.value === '' ? null : e.target.value)
          }
          label={label}
          disabled={loading}
        >
          <MenuItem value="">
            <em>Custom</em>
          </MenuItem>
          {personas.map(persona => (
            <MenuItem key={persona.key} value={persona.key}>
              {persona.key}
            </MenuItem>
          ))}
        </Select>
        {selectedPersona && (
          <FormHelperText>{selectedPersona.goodFor}</FormHelperText>
        )}
        {!selectedPersona && helperText && (
          <FormHelperText>{helperText}</FormHelperText>
        )}
      </FormControl>
    </Box>
  );
};
